import { Observable } from 'rxjs';

/**
 * **BrokerSubject**
 *
 * A custom RxJS Subject that acts as a broker with specific buffering behavior.
 *
 * - Buffers a single value when there are no subscribers and emits it only to the first subscriber.
 * - Subsequent subscribers do not receive the buffered value.
 * - When there are active subscribers, it emits values immediately without buffering.
 *
 * This class conforms to the RxJS Subject interface and can be used as a drop-in replacement
 * for standard subjects when this specific behavior is desired.
 */
export class BrokerSubject extends Observable {
  /**
   * Constructs a new BrokerSubject instance.
   */
  constructor() {
    super();
    /**
     * Holds the buffered value when there are no subscribers.
     * @private
     * @type {*}
     */
    this.buffer = null;

    /**
     * List of active observers/subscribers.
     * @private
     * @type {Array<Subscriber>}
     */
    this.observers = [];

    /**
     * Indicates if the broker has been stopped via `complete()`.
     * @private
     * @type {boolean}
     */
    this.isStopped = false;

    /**
     * Indicates if the broker has encountered an error via `error()`.
     * @private
     * @type {boolean}
     */
    this.hasError = false;

    /**
     * Stores the error if one has occurred.
     * @private
     * @type {*}
     */
    this.thrownError = null;
  }

  /**
   * Sends a value to subscribers or buffers it if there are none.
   * @param {*} value - The value to send to subscribers.
   * @example
   * broker.next('Event 1');
   */
  next(value) {
    if (this.isStopped || this.hasError) {
      return;
    }

    if (this.observers.length > 0) {
      // Emit to all active observers
      this.observers.forEach((observer) => {
        observer.next(value);
      });
      return;
    }

    // Buffer the value if there are no observers
    this.buffer = value;
  }

  /**
   * Notifies subscribers of an error and stops the broker.
   *
   * After calling `error()`, the broker will not emit any more values,
   * and all new subscribers will immediately receive the error notification.
   * @param {*} err - The error to notify subscribers about.
   * @example
   * broker.error(new Error('Something went wrong'));
   */
  error(err) {
    if (this.isStopped || this.hasError) {
      return;
    }

    this.hasError = true;
    this.thrownError = err;

    // Notify all observers of the error
    this.observers.forEach((observer) => {
      observer.error(err);
    });

    // Clear observers
    this.observers = [];
  }

  /**
   * Notifies subscribers of completion and stops the broker.
   *
   * After calling `complete()`, the broker will not emit any more values,
   * and all new subscribers will immediately receive the completion notification.
   * @example
   * broker.complete();
   */
  complete() {
    if (this.isStopped || this.hasError) {
      return;
    }

    this.isStopped = true;

    // Notify all observers of completion
    this.observers.forEach((observer) => {
      observer.complete();
    });

    // Clear observers
    this.observers = [];
  }

  /**
   * Internal method to handle subscriber subscriptions.
   *
   * This method is called when a new subscriber subscribes to the broker.
   *
   * - If the broker has encountered an error, the subscriber is immediately notified of the error.
   * - If the broker is completed, the subscriber is immediately notified of completion.
   * - If there is a buffered value and this is the first subscriber, the buffered value is emitted.
   * - The subscriber is added to the list of active observers to receive future emissions.
   * @typedef {import('rxjs').Subscriber } Subscriber
   * @param { Subscriber } subscriber - The subscriber to add.
   * @protected
   */
  _subscribe(subscriber) {
    if (this.hasError) {
      // Notify the subscriber of the error
      subscriber.error(this.thrownError);
      return;
    }
    if (this.isStopped) {
      // Notify the subscriber of completion
      subscriber.complete();
      return;
    }

    // Emit the buffered value to the first subscriber and clear the buffer
    if (this.buffer !== null && this.observers.length === 0) {
      subscriber.next(this.buffer);
      this.buffer = null;
    }

    // Add the subscriber to the list of observers
    this.observers.push(subscriber);

    // Add teardown logic to remove subscriber when they unsubscribe
    subscriber.add(() => {
      const index = this.observers.indexOf(subscriber);
      if (index !== -1) {
        this.observers.splice(index, 1);
      }
    });
  }
}
